import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import ModalHeader from '../components/ModalHeader';
import { setCurrentModal } from '../thunks/setCurrentModal';
import BadgeOptions from '../components/BadgeOptions';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import {
  determineLocationSessions,
  OrderingConstants,
  OrderingOperations,
  OrderingSelectors,
} from 'polygon-ordering';
import lodash from 'lodash';
import getLocationsWithDistance from '../selectors/getLocationsWithDistance';
import StandardButton from '../components/StandardButton';
import { Modal } from 'reactstrap';
import LocationSearch from '../components/LocationSearch';
import { Formik } from 'formik';
import Locations from '../components/Locations';
import EVENTS from '../constants/events';
import { logEvent } from '../utils/analytics';
import LocationListItem from '../components/LocationListItem';
import TableNumberReview from '../components/TableNumberReview';
import { adjustOrder } from '../thunks/adjustOrder';
import combineStyles from '../utils/combineStyles';
import getThemeLookup from '../selectors/getThemeLookup';
import { setInlineAlert } from '../slices/inlineAlert';

export const LOCATION_MODAL_ID = 'LOCATION_MODAL_ID';

const { fetchLocations, setTable, updateKeyOrderProperty } = OrderingOperations;
const { getOrderingWindowPadding, getKeyOrderPropertyUpdateInProgress } = OrderingSelectors;
const { SALE_TYPE, CATERING_SALE_TYPES } = OrderingConstants;

const LocationModal = ({ params }: { params: ModalDataParams }) => {
  const dispatch = useAppDispatch();
  const p = useAppSelector(getThemeLookup);
  const { t } = useTranslation();
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);

  // Modal params
  const { saleType, locationId, action, isReviewOrderScreen } = params;
  const previousModalId = useAppSelector(state => state.previousModalId);

  const keyOrderPropertyUpdateInProgress = useAppSelector(getKeyOrderPropertyUpdateInProgress);
  const orderingWindowPadding = useAppSelector(getOrderingWindowPadding);
  const [filteredLocations, setFilteredLocations] = useState<MapLocation[]>([]);
  const allLocations = useAppSelector(getLocationsWithDistance);
  const [page, setPage] = useState(action || locationId ? 2 : 1);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [allEnhancedLocations, setAllEnhancedLocations] = useState<MapLocation[]>([]);

  useEffect(() => {
    // I hate this, this is a hack fix that I did, something must have changed in merges, TODO: refactor everything with less boilerplate (600 hours)
    dispatch(updateKeyOrderProperty({}));
    dispatch(fetchLocations({}));
  }, []);

  useEffect(() => {
    syncAddress();
  }, [allLocations]);

  const syncAddress = async () => {
    const enhancedLocations = lodash.mapValues(allLocations, l => ({
      ...l,
      ...determineLocationSessions(l, orderingWindowPadding, saleType),
    })) as EnhancedLocations;

    const locationsFilteredBySaleType = lodash.pickBy(enhancedLocations, l =>
      l?.supportedSaleTypes?.includes(saleType as SALE_TYPE),
    );

    let locationsArray = lodash.orderBy(
      Object.keys(locationsFilteredBySaleType).map(id => ({
        ...enhancedLocations[id],
      })),
      ['distance'],
      ['asc'],
    );

    const nonFilterLocations = Object.keys(allLocations).map(id => ({ ...enhancedLocations[id] }));

    locationsArray = locationsArray.sort((a: any, b: any) => b?.online - a?.online);
    setFilteredLocations(locationsArray);
    setAllEnhancedLocations(nonFilterLocations);
  };

  return (
    <Modal
      isOpen={true}
      scrollable
      fullscreen={deviceTypeMobile ? true : false}
      toggle={() => dispatch(setCurrentModal(null))}
      // size="lg"
      fade={true}
      modalTransition={{
        baseClass: 'rc-scale-in',
        timeout: 0,
      }}
    >
      <Formik
        initialValues={{
          formatted_address: '',
          listView: true,
          locationId: (locationId as string) || '',
          resetMapLocation: false,
          table: '',
        }}
        onSubmit={() => {}}
      >
        {({ values, setFieldValue }) => {
          const selectedLocation = filteredLocations.filter(
            location => location.id === values.locationId,
          )[0];

          const isButtonDisabled = () => {
            const selectedLocation = filteredLocations.find(
              location => location.id === values.locationId,
            );
            const locationNotOnline = !selectedLocation?.online;

            // Handle table order and dine-in cases
            if (saleType === SALE_TYPE.TABLE_ORDER || saleType === SALE_TYPE.DINE_IN) {
              if (page !== 1) {
                return values.table === '' || requestInProgress;
              }
              return locationNotOnline || keyOrderPropertyUpdateInProgress;
            }

            // Handle catering case
            if (saleType && CATERING_SALE_TYPES.includes(saleType)) {
              return values.locationId === '' || keyOrderPropertyUpdateInProgress;
            }

            // Default case for other sale types
            return locationNotOnline || keyOrderPropertyUpdateInProgress;
          };

          return (
            <>
              <div
                style={combineStyles(
                  { width: '100%', paddingBottom: 10 },
                  p('locationModalBackground', ['backgroundColor']),
                )}
              >
                <ModalHeader
                  backClick={() => {
                    if (previousModalId) {
                      dispatch(
                        setCurrentModal({
                          modalId: previousModalId,
                        }),
                      );
                    } else if (action) {
                      dispatch(setCurrentModal(null));
                    } else {
                      if (
                        (saleType === SALE_TYPE.TABLE_ORDER || saleType === SALE_TYPE.DINE_IN) &&
                        page !== 1
                      ) {
                        setPage(page => page - 1);
                      } else {
                        dispatch(setCurrentModal(null));
                      }
                    }
                  }}
                  title={t(`saleType.${saleType}.name`)}
                />
                {page === 1 && (
                  <div style={deviceTypeMobile ? styles.badgeOptionsMobile : styles.badgeOptions}>
                    <LocationSearch />

                    <BadgeOptions
                      active={values.listView}
                      setActive={status => setFieldValue('listView', status)}
                      activeKey="button.listView"
                      inactiveKey="button.mapView"
                    />
                  </div>
                )}
              </div>
              <div
                style={combineStyles(
                  {
                    overflow: 'overlay',
                    height: '100vh',
                    paddingBottom: 100,
                  },
                  p('locationModalBackground', ['backgroundColor']),
                )}
              >
                {page === 1 && <Locations locations={filteredLocations} />}
                {page === 2 && (
                  <TableNumberReview
                    location={
                      allEnhancedLocations.filter(
                        location => location.id === String(values.locationId),
                      )[0]
                    }
                  />
                )}
              </div>

              <div className="position-absolute" style={{ bottom: 0, width: '100%', padding: 25 }}>
                {!values.listView && values.locationId !== '' && (
                  <LocationListItem
                    activateBgColor={values.listView && values.locationId === selectedLocation.id}
                    location={
                      filteredLocations.filter(location => location.id === values.locationId)[0]
                    }
                  />
                )}
                <StandardButton
                  label={t('button.continue')}
                  themeKey="continueButton"
                  onClick={async () => {
                    if (action) {
                      setRequestInProgress(true);
                      dispatch(setTable(values.table));
                      action();
                    } else {
                      if (saleType === SALE_TYPE.TABLE_ORDER || saleType === SALE_TYPE.DINE_IN) {
                        if (page === 1) {
                          setPage(page + 1);
                        } else {
                          dispatch(setTable(values.table));
                          setRequestInProgress(true);
                          dispatch(
                            adjustOrder({
                              saleType,
                              locationId: (locationId as string) || values.locationId,
                            }),
                          ).catch(() => setRequestInProgress(false));

                          logEvent(EVENTS.PICK_LOCATION_AND_SALE_TYPE, {
                            label: `ST:${saleType} - LOC:${values.locationId}`,
                          });
                          logEvent(EVENTS.SET_TABLE_NUMBER);
                        }
                      } else if (
                        saleType &&
                        CATERING_SALE_TYPES.includes(saleType) &&
                        !isReviewOrderScreen
                      ) {
                        dispatch(
                          updateKeyOrderProperty({
                            autoApply: false,
                            locationId: values.locationId,
                            saleType,
                          }),
                        );
                        dispatch(
                          setCurrentModal({
                            modalId: previousModalId,
                          }),
                        );
                        dispatch(setInlineAlert({ visible: false }));
                      } else {
                        dispatch(
                          adjustOrder({
                            saleType,
                            locationId: values.locationId,
                            updateLoadingDependency: false,
                          }),
                        );
                        logEvent(EVENTS.PICK_LOCATION_AND_SALE_TYPE, {
                          label: `ST:${saleType} - LOC:${values.locationId}`,
                        });
                      }
                    }
                  }}
                  disabled={isButtonDisabled()}
                  showSpinner={keyOrderPropertyUpdateInProgress}
                  containerStyle={{ marginLeft: '10%', marginRight: '10%', borderRadius: 5 }}
                />
              </div>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default LocationModal;

const styles: Styles = {
  modalContainer: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    position: 'relative',
  },
  modalMobileContainer: {
    // https://stackoverflow.com/a/30523342/8706204
    WebkitMaskImage: '-webkit-radial-gradient(white, black)',
    borderRadius: 0,
  },
  badgeOptions: {
    paddingTop: 10,
    marginLeft: '10%',
    marginRight: '10%',
    gap: '0.5em',
    flexDirection: 'column',
    display: 'flex',
  },
  badgeOptionsMobile: {
    marginLeft: 10,
    marginRight: 10,
    gap: '0.5em',
    flexDirection: 'column',
    display: 'flex',
  },
};
